import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarPlus, RefreshCw, Pencil, Trash2, X, AlertTriangle, Rss, Bell } from 'lucide-react'
import type { CalendarPreset, CalendarSubscriptionDto } from '@familynest/shared'
import { api } from '../../lib/api'
import { useLive } from '../../lib/ws'
import { Button, useBackdropClose } from '../ui'
import { useDialogs } from '../ui/dialogs'

const field = 'w-full rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]'
const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const

// Kräftige, gut unterscheidbare Kalenderfarben (Müll, Feiertage, Ferien …).
const CAL_COLORS = ['#3f3f46', '#c0392b', '#2e86de', '#27ae60', '#8e44ad', '#e67e22', '#16a085', '#d81b60']

// Standard-Erinnerung für Abo-Termine (ADR-089) — Müll typischerweise am Vorabend.
const REMINDER_OPTS: Array<{ v: number | null; label: string }> = [
  { v: null, label: 'Keine Erinnerung' },
  { v: 60, label: '1 Std vorher' },
  { v: 300, label: 'Vorabend 19 Uhr (bei Ganztags-Terminen)' },
  { v: 720, label: '12 Std vorher' },
  { v: 1440, label: '1 Tag vorher' },
]

function reminderLabel(v: number | null | undefined): string | null {
  if (v == null) return null
  return REMINDER_OPTS.find((o) => o.v === v)?.label ?? `${v} Min vorher`
}

function fmtWhen(iso: string | null): string {
  if (!iso) return 'noch nie'
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} Uhr`
}

/**
 * Verwaltung abonnierter externer iCal-Kalender (Müll, Feiertage, Ferien).
 * Nur für Eltern erreichbar (in den Einstellungen). Termine erscheinen
 * schreibgeschützt und farbig im Familienkalender; Aktualisierung automatisch
 * alle 30 min (ADR-089, mit ETag + Backoff bei Fehlern) und manuell per
 * „Aktualisieren". Vorlagen für Feiertage/Schulferien je Bundesland.
 */
export function SubscriptionsScreen(): React.JSX.Element {
  const dialogs = useDialogs()
  const [items, setItems] = useState<CalendarSubscriptionDto[]>([])
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog] = useState<{ editing: CalendarSubscriptionDto | null } | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setItems(await api.listSubscriptions())
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])
  useLive('calendar', load)

  async function sync(id: string): Promise<void> {
    setBusyId(id)
    try {
      await api.syncSubscription(id)
      await load()
    } finally {
      setBusyId(null)
    }
  }
  async function remove(s: CalendarSubscriptionDto): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Abo löschen', message: `Abo „${s.name}" löschen? Alle daraus importierten Termine werden entfernt.`, confirmLabel: 'Löschen', danger: true }))) return
    await api.deleteSubscription(s.id)
    await load()
  }
  async function toggle(s: CalendarSubscriptionDto): Promise<void> {
    setBusyId(s.id)
    try {
      await api.updateSubscription(s.id, { enabled: !s.enabled })
      await load()
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl p-4">
      <div className="mb-1 flex items-center justify-between">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--color-ink)' }}>Kalender-Abos</h1>
        <Button onClick={() => setDialog({ editing: null })}>
          <CalendarPlus className="size-4" /> Abo hinzufügen
        </Button>
      </div>
      <p className="mb-4 text-sm" style={{ color: 'var(--color-muted)' }}>
        Externe iCal-Kalender (Müll, Feiertage, Ferien) per URL abonnieren. Termine erscheinen automatisch im Familienkalender und werden regelmäßig aktualisiert.
      </p>

      {loading ? null : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
          <Rss className="size-8" />
          Noch keine Kalender abonniert. Tippe „Abo hinzufügen" und füge z. B. die iCal-URL deines Müllkalenders ein.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((s) => (
            <li key={s.id} className="flex items-center gap-3 rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)', opacity: s.enabled ? 1 : 0.55 }}>
              <span className="size-4 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>{s.name}</span>
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)' }}>{s.eventCount} Termine</span>
                  {!s.enabled && <span className="shrink-0 text-xs" style={{ color: 'var(--color-muted)' }}>pausiert</span>}
                  {s.renderAsBackground && <span className="shrink-0 text-xs" style={{ color: 'var(--color-muted)' }}>Hintergrund</span>}
                  {reminderLabel(s.defaultReminderMinutes) && (
                    <span className="flex shrink-0 items-center gap-0.5 text-xs" style={{ color: 'var(--color-muted)' }} title={`Erinnerung: ${reminderLabel(s.defaultReminderMinutes)}`}>
                      <Bell className="size-3" />
                    </span>
                  )}
                </div>
                <div className="truncate text-xs" style={{ color: 'var(--color-muted)' }}>Aktualisiert: {fmtWhen(s.lastSyncAt)}</div>
                {s.lastError && (
                  <div className="mt-0.5 flex items-center gap-1 text-xs" style={{ color: 'var(--color-danger)' }}>
                    <AlertTriangle className="size-3" /> {s.lastError}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => void sync(s.id)} disabled={busyId === s.id} aria-label="Aktualisieren" title="Jetzt aktualisieren" className="rounded p-1.5 hover:bg-black/5">
                  <RefreshCw className={`size-4 ${busyId === s.id ? 'animate-spin' : ''}`} style={{ color: 'var(--color-muted)' }} />
                </button>
                <button onClick={() => void toggle(s)} disabled={busyId === s.id} className="rounded px-2 py-1 text-xs hover:bg-black/5" style={{ color: 'var(--color-muted)' }}>
                  {s.enabled ? 'Pause' : 'Aktiv'}
                </button>
                <button onClick={() => setDialog({ editing: s })} aria-label="Bearbeiten" className="rounded p-1.5 hover:bg-black/5"><Pencil className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                <button onClick={() => void remove(s)} aria-label="Löschen" className="rounded p-1.5 hover:bg-black/5"><Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {dialog && (
        <SubscriptionDialog
          editing={dialog.editing}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null)
            await load()
          }}
        />
      )}
    </div>
  )
}

function SubscriptionDialog({ editing, onClose, onSaved }: { editing: CalendarSubscriptionDto | null; onClose: () => void; onSaved: () => Promise<void> }): React.JSX.Element {
  const [name, setName] = useState(editing?.name ?? '')
  const [url, setUrl] = useState(editing?.url ?? '')
  const [color, setColor] = useState(editing?.color ?? CAL_COLORS[0]!)
  const [renderAsBackground, setRenderAsBackground] = useState(editing?.renderAsBackground ?? false)
  const [reminder, setReminder] = useState<number | null>(editing?.defaultReminderMinutes ?? null)
  const [presetKey, setPresetKey] = useState<string | null>(editing?.presetKey ?? null)
  const [presets, setPresets] = useState<CalendarPreset[]>([])
  const [hint, setHint] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Vorlagen nur beim Neuanlegen anbieten (ADR-089).
  useEffect(() => {
    if (editing) return
    api.listSubscriptionPresets().then(setPresets).catch(() => setPresets([]))
  }, [editing])
  const groups = useMemo(() => {
    const m = new Map<string, CalendarPreset[]>()
    for (const p of presets) m.set(p.group, [...(m.get(p.group) ?? []), p])
    return [...m.entries()]
  }, [presets])

  function applyPreset(key: string): void {
    const p = presets.find((x) => x.key === key)
    if (!p) {
      setPresetKey(null)
      setHint(null)
      return
    }
    setPresetKey(p.key)
    setName(p.name)
    setUrl(p.url)
    setColor(p.color)
    setRenderAsBackground(p.renderAsBackground)
    setReminder(null)
    setHint(p.hint ?? null)
  }

  const valid = name.trim().length > 0 && /^(https?|webcal):\/\//i.test(url.trim())

  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const payload = { name: name.trim(), url: url.trim(), color, renderAsBackground, defaultReminderMinutes: reminder, presetKey }
      if (editing) await api.updateSubscription(editing.id, payload)
      else await api.createSubscription(payload)
      await onSaved()
    } catch {
      setError('Speichern fehlgeschlagen. Bitte URL prüfen (muss eine erreichbare .ics-Adresse sein).')
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>
            <Rss className="size-5" style={{ color: 'var(--color-accent)' }} /> {editing ? 'Abo bearbeiten' : 'Kalender abonnieren'}
          </h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="flex flex-col gap-3">
          {!editing && groups.length > 0 && (
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Vorlage (optional)</span>
              <select className={field} style={fieldStyle} value={presetKey ?? ''} onChange={(e) => applyPreset(e.target.value)}>
                <option value="">— eigene URL —</option>
                {groups.map(([g, list]) => (
                  <optgroup key={g} label={g}>
                    {list.map((p) => (
                      <option key={p.key} value={p.key}>{p.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {hint && <span className="text-xs" style={{ color: 'var(--color-muted)' }}>{hint}</span>}
            </label>
          )}
          <input className={field} style={fieldStyle} placeholder="Name (z. B. Müll Musterstadt)" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>iCal-URL (.ics oder webcal://)</span>
            <input className={field} style={fieldStyle} placeholder="https://…/kalender.ics" value={url} onChange={(e) => setUrl(e.target.value)} />
          </label>
          <div>
            <span className="mb-1 block text-sm" style={{ color: 'var(--color-muted)' }}>Farbe im Kalender</span>
            <div className="flex flex-wrap gap-2">
              {CAL_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)} className="size-7 rounded-full" style={{ backgroundColor: c, outline: color === c ? '2px solid var(--color-ink)' : 'none', outlineOffset: 2 }} aria-label={c} />
              ))}
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm" style={{ color: 'var(--color-ink)' }}>
            <input type="checkbox" className="mt-0.5" checked={renderAsBackground} onChange={(e) => setRenderAsBackground(e.target.checked)} />
            <span>
              Als Hintergrund anzeigen
              <span className="block text-xs" style={{ color: 'var(--color-muted)' }}>Tage dezent einfärben statt Balken — passend für Feiertage und Ferien.</span>
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Erinnerung (geht an alle Erwachsenen)</span>
            <select className={field} style={fieldStyle} value={reminder === null ? '' : String(reminder)} onChange={(e) => setReminder(e.target.value === '' ? null : Number(e.target.value))}>
              {REMINDER_OPTS.map((o) => (
                <option key={o.label} value={o.v === null ? '' : String(o.v)}>{o.label}</option>
              ))}
            </select>
          </label>
          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Die Termine werden schreibgeschützt übernommen und alle 30 Minuten aktualisiert. Beim Speichern wird sofort einmal abgerufen.</p>
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={save} disabled={busy || !valid}>{editing ? 'Speichern' : 'Abonnieren'}</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
